import ColumnsButton from "../../components/common/ColumnsButton";
import React, { useState, useContext, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import UserToolbar from "../../components/users/UserToolbar";
import { toast } from "../../utils/toast";
import AppContext from "../../context/AppContext";
import { getUserInfoAPI, updateUserStatus, searchUsers, fetchUserColumnValues, fetchUserGridColumns, UserGridError, USER_SORTABLE_COLUMNS } from "../../service/User";
import ColumnValueFilterList from "../../components/common/ColumnValueFilterList";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  countActiveColumnFilters,
  isMeaningfulColumnFilter,
  makeBetweenFilter,
  makeContainsFilter,
  makeInFilter,
  MAX_COLUMN_FILTERS,
  serializeColumnFilters,
  TEXT_FILTER_DEBOUNCE_MS,
} from "../../utils/taskListFilters";
import { Spinner } from "../../Spinner";
import translations from "../../components/common/Translations";
import { getPermissionByScreenCode, hasScreenAccess } from "../../utils/permissions";
import useDirection from "../../hooks/useDirection";
import { dirForLang, isRTL } from "../../utils/rtl";
import PageSizeSelector from "../../components/common/PageSizeSelector";
import { FiEye } from "react-icons/fi";
import { Pencil, Columns3, RotateCcw, UserPlus, GripVertical, Search, Filter, ChevronUp, ChevronDown, ChevronsUpDown, Inbox, SearchX, Check } from "lucide-react";
import AddUser from "./AddUser";
import ActionIconButton from "../../components/common/ActionIconButton";
import ModalCloseButton from "../../components/common/ModalCloseButton";
import PagerArrowButton from "../../components/common/PagerArrowButton";
import CommonButton from "../../components/common/CommonButton";
import CommonTooltip from "../../components/common/CommonTooltip";
import HoverTooltip from "../../components/common/HoverTooltip";
import OverflowTooltipText from "../../components/common/OverflowTooltipText";
import ColumnsCustomizeModal from "../../components/common/ColumnsCustomizeModal";

const HOVER_TOOLTIP_EDGE_MARGIN = 10;
const HOVER_TOOLTIP_GAP = 8;

// Same portal + fixed-position + flip-above-when-clipped-below technique as
// HoverTooltip above, but for popovers that render arbitrary list content
// (not just a text string) anchored to a `left-align to trigger` edge instead
// of centered. Used by MultiValueCell/PeoplePreview's "+N" popovers so they
// can never be clipped by the table's scrolling container — without this,
// rows near the bottom of the table would have their popover cut off instead
// of flipping to render above the trigger.
//
// The width is a fixed Tailwind class (`w-56`), not an inline style computed
// after measuring — measuring `tooltipRef` while the box still had no width
// constraint at all (only `position: fixed`, no `top`/`left`/`width`) made
// the browser fall back to the unpositioned box's "static position", which
// for a plain block appended at the end of `<body>` sits pinned near the
// document's top-left corner rather than anywhere near the trigger. Giving
// it a width from the very first render makes every measurement consistent.
// Also listens for scroll/resize while open (same as ColumnFilterPopover)
// so it tracks the trigger if the table's own scroll container moves under
// it, instead of freezing at its mount-time position.
const HoverListPopover = ({ isOpen, anchorRef, children }) => {
  const tooltipRef = useRef(null);
  const [style, setStyle] = useState({ position: "fixed", visibility: "hidden" });

  useLayoutEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      if (!anchorRef?.current || !tooltipRef.current) return;
      const triggerRect = anchorRef.current.getBoundingClientRect();
      const tipRect = tooltipRef.current.getBoundingClientRect();

      const fitsBelow = triggerRect.bottom + HOVER_TOOLTIP_GAP + tipRect.height + HOVER_TOOLTIP_EDGE_MARGIN <= window.innerHeight;
      const top = fitsBelow
        ? triggerRect.bottom + HOVER_TOOLTIP_GAP
        : Math.max(HOVER_TOOLTIP_EDGE_MARGIN, triggerRect.top - tipRect.height - HOVER_TOOLTIP_GAP);

      let left = triggerRect.left;
      const maxLeft = window.innerWidth - tipRect.width - HOVER_TOOLTIP_EDGE_MARGIN;
      left = Math.min(Math.max(left, HOVER_TOOLTIP_EDGE_MARGIN), Math.max(maxLeft, HOVER_TOOLTIP_EDGE_MARGIN));

      setStyle({ position: "fixed", top, left, visibility: "visible" });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen, anchorRef]);

  if (!isOpen) return null;

  return createPortal(
    <div ref={tooltipRef} style={style} className="z-[60] w-72 max-w-[calc(100vw-20px)] bg-white rounded-lg border border-slate-200 shadow-lg p-2 whitespace-normal text-start">
      {children}
    </div>,
    document.body
  );
};

// Keeps a hover popover open while the cursor crosses the small visual gap
// between the trigger and the popover itself. Without the delay, the gap sits
// outside the wrapper's box (it belongs to the wrapper's *ancestor*, not a
// descendant), so the browser fires mouseleave — and React unmounts the
// popover — before the cursor ever reaches it. Delaying the close lets the
// popover survive that transit; re-entering (the wrapper or the popover, both
// part of the same subtree) cancels the pending close.
const useDelayedHover = (delay = 150) => {
  const [hover, setHover] = useState(false);
  const closeTimerRef = useRef(null);

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const onMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setHover(true);
  };

  const onMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => setHover(false), delay);
  };

  const close = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setHover(false);
  };

  return { hover, onMouseEnter, onMouseLeave, close };
};

// Full, searchable list of a multi-value cell's names (Department/Project),
// opened from the hover popover's "View all" button. Every name wraps onto as
// many lines as it needs — nothing is truncated here.
const ValueListModal = ({ title, names, onClose }) => {
  const { langMode } = useContext(AppContext);
  const t = translations[langMode] || {};
  const [q, setQ] = useState("");
  const rtl = isRTL(langMode);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const list = names.filter((n) => n.toLowerCase().includes(q.trim().toLowerCase()));

  return createPortal(
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/40 p-4"
      dir={dirForLang(langMode)}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onClick={(e) => e.stopPropagation()}
    >
      <div role="dialog" aria-modal="true" aria-label={title} className="flex max-h-[80vh] w-full max-w-md flex-col rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <h3 className="text-sm font-semibold text-slate-800">
            {title} <span className="font-normal text-slate-400">({names.length})</span>
          </h3>
          <ModalCloseButton onClick={onClose} size={16} className="h-8 w-8" />
        </div>
        {names.length > 5 && (
          <div className="relative px-4 pt-3">
            <Search size={14} className={`absolute top-1/2 mt-1.5 -translate-y-1/2 text-slate-400 ${rtl ? "right-6" : "left-6"}`} />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t.userFilterSearch || "Search…"}
              className={`w-full rounded-md border border-slate-200 py-1.5 text-sm outline-none ${rtl ? "pr-8 pl-2" : "pl-8 pr-2"} focus:border-[#236DB4] focus:ring-2 focus:ring-[#236DB4]/20`}
            />
          </div>
        )}
        <ul className="custom-scrollbar flex-1 space-y-1 overflow-y-auto px-4 py-3">
          {list.length === 0 && <li className="py-2 text-xs text-slate-400">{t.userFilterNoMatches || "No matches"}</li>}
          {list.map((n, i) => (
            <li key={`${n}-${i}`} className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700 whitespace-normal [overflow-wrap:anywhere]">
              {n}
            </li>
          ))}
        </ul>
      </div>
    </div>,
    document.body
  );
};
// Shows up to 2 resolved names + a "+N" overflow badge with a hover popover
// listing the rest. Used for Department/Project/Group columns, which are
// multi-valued (a user can belong to many departments/projects/groups).
//
// The popover lists every name in full (wrapped, never truncated) and has a
// "View all" button that opens ValueListModal for long lists / long names.
// A single value just uses the overflow tooltip — nothing to list.
const POPOVER_PREVIEW_LIMIT = 5;
// `names` are display names straight from the get-users *Details fields.
const MultiValueCell = ({ names, emptyLabel }) => {
  const { langMode } = useContext(AppContext);
  const { hover, onMouseEnter, onMouseLeave, close } = useDelayedHover();
  const triggerRef = useRef(null);
  const [showAll, setShowAll] = useState(false);
  if (!names || names.length === 0) return <span className="text-slate-400">—</span>;

  if (names.length === 1) {
    return <OverflowTooltipText text={names[0]} className="max-w-full align-middle" />;
  }

  const shown = names.slice(0, 2);
  const extra = names.length - shown.length;
  const preview = names.slice(0, POPOVER_PREVIEW_LIMIT);
  const hiddenInPopover = names.length - preview.length;
  const viewAllLabel = translations[langMode]?.userViewAll || "View all";

  return (
    <div ref={triggerRef} className="relative inline-flex items-center gap-1.5 max-w-full min-w-0 cursor-default" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <span className="truncate min-w-0 max-w-[160px] inline-block align-middle">{shown.join(", ")}</span>
      {extra > 0 && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-semibold shrink-0 bg-slate-100 text-slate-500">
          +{extra}
        </span>
      )}
      <HoverListPopover isOpen={hover && !showAll} anchorRef={triggerRef}>
        <div className="text-[11px] font-semibold px-1.5 pb-1.5 text-slate-400 uppercase tracking-wide">{emptyLabel} ({names.length})</div>
        <ul className="space-y-1 max-h-56 overflow-y-auto overflow-x-hidden custom-scrollbar">
          {preview.map((n, i) => (
            <li key={`${n}-${i}`} className="px-1.5 py-1 rounded-md text-sm leading-snug text-slate-700 whitespace-normal [overflow-wrap:anywhere]">{n}</li>
          ))}
        </ul>
        <div className="mt-1.5 pt-1.5 border-t border-slate-100 flex items-center justify-between gap-2 px-1.5">
          <span className="text-[11px] text-slate-400">{hiddenInPopover > 0 ? `+${hiddenInPopover}` : ""}</span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); close(); setShowAll(true); }}
            className="text-xs font-semibold text-[#236DB4] hover:underline"
          >
            {viewAllLabel} ({names.length})
          </button>
        </div>
      </HoverListPopover>
      {showAll && <ValueListModal title={emptyLabel} names={names} onClose={() => { setShowAll(false); close(); }} />}
    </div>
  );
};

function nameToHslColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return `hsl(${Math.abs(hash) % 360} 55% 45%)`;
}

function initials(name) {
  return (name || "")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

// Small colored initial-avatar chip row with a "+N" overflow + hover popover
// listing the rest. Used below the group name(s) to preview member names.
const PeoplePreview = ({ names, emptyLabel }) => {
  const { hover, onMouseEnter, onMouseLeave } = useDelayedHover();
  const triggerRef = useRef(null);
  if (!names || names.length === 0) return <span className="text-slate-400 text-xs">—</span>;
  const shown = names.slice(0, 2);
  const extra = names.length - shown.length;

  return (
    <div ref={triggerRef} className="relative inline-flex items-center gap-1.5" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div className="flex items-center shrink-0">
        {shown.map((n, i) => (
          <div
            key={`${n}-${i}`}
            className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold text-white border-2 border-white ${i > 0 ? "-ml-1.5" : ""}`}
            style={{ background: nameToHslColor(n) }}
          >
            {n[0]?.toUpperCase()}
          </div>
        ))}
      </div>
      {extra > 0 && (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[11px] font-semibold shrink-0 bg-slate-100 text-slate-500">
          +{extra}
        </span>
      )}
      <HoverListPopover isOpen={hover} anchorRef={triggerRef}>
        <div className="text-[11px] font-semibold px-1.5 pb-1.5 text-slate-400 uppercase tracking-wide">{emptyLabel} ({names.length})</div>
        <div className="space-y-1 max-h-48 overflow-y-auto overflow-x-hidden custom-scrollbar">
          {names.map((n, i) => (
            <div key={`${n}-${i}`} className="flex items-center gap-2 px-1.5 py-1 rounded-md text-sm text-slate-700">
              <div
                className="w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-semibold text-white shrink-0"
                style={{ background: nameToHslColor(n) }}
              >
                {n[0]?.toUpperCase()}
              </div>
              <span className="min-w-0 flex-1 break-words">{n}</span>
            </div>
          ))}
        </div>
      </HoverListPopover>
    </div>
  );
};

// Group columns: bold group name(s) on top, member-preview avatars below.
// `groups` are get-users groupDetails / groupListDetails entries
// ({ groupId, groupName, members: [{ userId, userName }] }) — members come
// with the row, so no extra call is needed to expand a group.
const GroupCell = ({ groups, membersLabel }) => {
  if (!groups || groups.length === 0) return <span className="text-slate-400">—</span>;
  const names = groups.map((g) => g?.groupName || `#${g?.groupId}`);
  const memberNames = Array.from(
    new Set(groups.flatMap((g) => (Array.isArray(g?.members) ? g.members : []).map((m) => m?.userName).filter(Boolean)))
  );

  return (
    <div className="flex min-w-0 w-full flex-col items-center py-0.5 text-center">
      <OverflowTooltipText text={names.join(", ")} className="w-full max-w-[140px] text-center text-sm font-medium text-slate-800" />
      <div className="mt-1 min-w-0 max-w-[140px]"><PeoplePreview names={memberNames} emptyLabel={membersLabel} /></div>
    </div>
  );
};

// Per-column funnel popover. Positioned/closed the same way as Task
// Management's HeaderFilterPopover — portal to <body>, fixed position
// computed from the funnel button's own rect, close on outside click/Escape —
// so the table's scroll container can't clip it. The content is the shared
// ColumnValueFilterList (same filter format, operators and "(Blanks)" rule as
// the task-list grid).
const COLUMN_FILTER_POPOVER_WIDTH = 248;
// Date funnels hold the inline calendar, so they use the task list's wider date popover.
const DATE_FILTER_POPOVER_WIDTH = 300;
const ColumnFilterPopover = ({ onClose, anchorEl, children, width = COLUMN_FILTER_POPOVER_WIDTH }) => {
  const [popoverStyle, setPopoverStyle] = useState(null);
  const popoverRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      const rect = anchorEl?.getBoundingClientRect();
      if (!rect) return;
      const viewportPadding = 8;
      const left = Math.min(
        Math.max(viewportPadding, rect.right - width),
        window.innerWidth - width - viewportPadding
      );
      setPopoverStyle({ top: rect.bottom + 6, left });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [anchorEl, width]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (popoverRef.current?.contains(event.target)) return;
      if (anchorEl?.contains(event.target)) return;
      onClose?.();
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [onClose, anchorEl]);

  if (!popoverStyle) return null;

  return createPortal(
    <div
      ref={popoverRef}
      style={{ position: "fixed", top: popoverStyle.top, left: popoverStyle.left, width }}
      className="z-[80] bg-white rounded-lg border border-slate-200 shadow-xl p-3 normal-case font-normal tracking-normal text-slate-700"
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body
  );
};

const parseLocalYmd = (s) => {
  if (!s) return null;
  const parts = String(s).split("-").map(Number);
  if (parts.length < 3 || parts.some((n) => Number.isNaN(n))) return null;
  return new Date(parts[0], parts[1] - 1, parts[2]);
};
const formatLocalYmd = (date) => {
  if (!date) return "";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
const isValidYmd = (s) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const d = parseLocalYmd(s);
  const [y, m, day] = s.split("-").map(Number);
  return d.getFullYear() === y && d.getMonth() === m - 1 && d.getDate() === day;
};
const formatRangeText = (from, to) => (from || to ? `${from || "—"} to ${to || "—"}` : "");

// Date-range funnel — same UI and behaviour as the task list's date column
// filter (TaskListView's DateColumnFilter): a typed "YYYY-MM-DD to YYYY-MM-DD"
// box plus an inline range calendar. Picking/typing only edits a draft; the
// filter is sent when Apply is clicked (Clear empties the draft, Apply then
// removes the filter). Sent as op "between" (inclusive, either bound optional).
const DateRangeColumnFilter = ({ from, to, onApply }) => {
  const { langMode } = useContext(AppContext);
  const t = translations[langMode] || {};
  const [draft, setDraft] = useState({ from: from || "", to: to || "" });
  const currentFrom = draft.from;
  const currentTo = draft.to;
  const onChange = (nextFrom, nextTo) => setDraft({ from: nextFrom, to: nextTo });
  const apply = () => {
    // Typed text can give a reversed range; read it the way it was meant.
    const [lo, hi] = currentFrom && currentTo && currentFrom > currentTo ? [currentTo, currentFrom] : [currentFrom, currentTo];
    onApply(lo, hi);
  };
  const [inputText, setInputText] = useState(formatRangeText(currentFrom, currentTo));

  // Keep the text box in sync when the range changes from the calendar or Clear.
  useEffect(() => {
    setInputText((prev) => {
      const matches = prev.match(/\d{4}-\d{2}-\d{2}/g) || [];
      if ((matches[0] || "") === currentFrom && (matches[1] || "") === currentTo) return prev;
      return formatRangeText(currentFrom, currentTo);
    });
  }, [currentFrom, currentTo]);

  const handleInputChange = (e) => {
    const val = e.target.value;
    setInputText(val);
    if (!val.trim()) {
      onChange("", "");
      return;
    }
    const [parsedStart = "", parsedEnd = ""] = val.match(/\d{4}-\d{2}-\d{2}/g) || [];
    let nextFrom = currentFrom;
    let nextTo = currentTo;
    if (parsedStart && isValidYmd(parsedStart)) nextFrom = parsedStart;
    else if (!parsedStart) nextFrom = "";
    if (parsedEnd && isValidYmd(parsedEnd)) nextTo = parsedEnd;
    else if (!parsedEnd) nextTo = "";
    if (nextFrom !== currentFrom || nextTo !== currentTo) onChange(nextFrom, nextTo);
  };

  return (
    <div className="w-full flex flex-col items-center">
      <div className="w-full relative mb-2 text-start">
        <input
          type="text"
          value={inputText}
          onChange={handleInputChange}
          placeholder={t.selectDateRangeHint ?? "Select date range (YYYY-MM-DD to YYYY-MM-DD)"}
          className="w-full min-w-0 h-8 text-xs border rounded-lg px-2.5 bg-slate-50 placeholder:text-slate-400 text-slate-700 font-medium border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#236DB4]/20 focus:border-[#236DB4]"
        />
      </div>
      <div className="react-datepicker-inline-container border border-slate-100 rounded-lg overflow-hidden shadow-sm">
        <DatePicker
          selected={null}
          startDate={parseLocalYmd(currentFrom)}
          endDate={parseLocalYmd(currentTo)}
          onChange={(dates) => {
            const [start, end] = dates || [];
            onChange(formatLocalYmd(start), formatLocalYmd(end));
          }}
          selectsRange
          inline
        />
      </div>
      <div className="w-full flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
        <button
          type="button"
          onClick={() => onChange("", "")}
          className="text-[11px] font-medium text-slate-400 hover:text-slate-600 hover:underline transition-colors"
        >
          {t.clear ?? "Clear"}
        </button>
        <button
          type="button"
          onClick={apply}
          className="px-2.5 py-1 text-[11px] font-semibold text-white bg-[#236DB4] rounded-md hover:bg-[#1d5a96] transition-all shadow-sm"
        >
          {t.apply ?? "Apply"}
        </button>
      </div>
    </div>
  );
};

// Matches the prototype's design-token badge colors (T.primary/indigo/warn for
// admin/office_user/field_agent); supervisor/super_admin extend the same palette
// since the prototype's mock dataset didn't include those two roles.
const USER_TYPE_BADGE_STYLES = {
  admin: { color: "#1D5FAE", bg: "#EAF2FB" },
  office_user: { color: "#5346C2", bg: "#EEEBFB" },
  field_agent: { color: "#B4680B", bg: "#FDF1E0" },
  supervisor: { color: "#0F7B6C", bg: "#E1F5F0" },
  super_admin: { color: "#C22B3B", bg: "#FBE9EB" },
};

// At least this many toggleable columns (excluding the pinned User/Action
// columns, which are always shown) must stay visible — None still leaves the
// first two of the current column order checked instead of emptying the
// table down to just the pinned columns; unchecking a box that would drop
// below this is blocked live.
const MIN_VISIBLE_COLUMNS = 2;

// Persisted table view (column funnel filters, visible columns, drag order) —
// survives refresh, opening a new window/tab, and logging back in. Read once
// at mount via a lazy useState initializer, saved back on change via a
// debounced effect, and cleared only by the toolbar Reset action. Mirrors the
// same pattern already used for the Task list's column layout/filters
// persistence (see TaskListView.js's readTaskManageTableLayout /
// TASK_MANAGE_TABLE_*_KEY) — global, not scoped per organization, matching
// that precedent.
const USER_MANAGE_TABLE_VIEW_KEY = "userManageTableView";

// Group membership comes in two shapes from get-users depending on role, and
// they mean different things, so they are shown as separate columns:
// - admin / supervisor / office_user: `groupListDetails` — whole groups
//   *assigned* to that user ("Assigned Groups" column, id "group").
// - field_agent: `groupDetails` — the single group the agent *belongs to*
//   ("Agent Group" column, id "agentGroup"). The key is omitted entirely when
//   the agent has no group, so undefined is the normal "none" case.
// The list rows no longer carry the raw id lists (groupList, projectList, …).
const assignedGroupsOf = (u) => (Array.isArray(u?.groupListDetails) ? u.groupListDetails.filter(Boolean) : []);
const agentGroupsOf = (u) => (u?.groupDetails ? [u.groupDetails] : []);
// projectDetails / departmentDetails / workflowDetails / roleDetails: [{ id, name }].
const detailNames = (details) => (Array.isArray(details) ? details.filter(Boolean).map((d) => d?.name || `#${d?.id}`) : []);

// Backend column keys (GET /get-users/columns, columnFilters, sortBy).
// Sorting covers every filtered user server-side, but only on these keys —
// the details columns and lastLogin are rejected by the backend.
const SORTABLE_API_KEYS = USER_SORTABLE_COLUMNS;
// Before /columns has loaded: every column with a backend key is filterable
// except organization (sortable only).
const NON_FILTERABLE_API_KEYS = new Set([]);
// Columns that only appear when the columns API reports them as accessible.
const METADATA_ONLY_COLUMN_IDS = new Set(["email", "employeeId", "workflow", "role"]);

// Toolbar search fields. "All Fields" is sent as body.search (the backend
// matches name, email, phone and employee id); a specific field is sent as a
// `contains` column filter on that backend column.
const USER_SEARCH_SCOPES = [
  { value: "all", labelKey: "allFields", fallback: "All Fields", apiKey: null },
  { value: "username", labelKey: "username", fallback: "Username", apiKey: "name" },
  { value: "email", labelKey: "email", fallback: "Email", apiKey: "email" },
  { value: "phone", labelKey: "Phone", fallback: "Phone", apiKey: "phone" },
  { value: "employeeId", labelKey: "userColEmployeeId", fallback: "Employee ID", apiKey: "employeeId" },
];

// Filter state uses the task-list model: Record<apiKey, ColumnFilter>
// (see utils/taskListFilters). v4 moved the details columns from id filters
// (groupList, projectList, …) to name filters on the *Details keys, so older
// saved filters are dropped rather than misread.
const USER_MANAGE_TABLE_VIEW_SCHEMA_VERSION = 4;
// Filter keys the backend no longer accepts; saved filters on them are dropped on load.
const REMOVED_FILTER_KEYS = new Set([
  "areaCode", "subContract", "loginCount", "groupName",
  "groupList", "groupId", "projectList", "departmentList", "workflowList", "roleIds",
]);

const DEFAULT_VISIBLE_COLS = {
  userType: true, status: true, department: true, group: true, agentGroup: true, project: true,
  phone: false, organization: false, lastLogin: false,
  email: false, employeeId: false, workflow: false, role: false,
};
const DEFAULT_COLUMN_ORDER = [
  "userType", "status", "department", "group", "agentGroup", "project", "phone", "lastLogin", "organization",
  "email", "employeeId", "workflow", "role",
];
const CENTERED_COLUMN_IDS = new Set([
  "userType", "phone", "status", "lastLogin", "action", "department", "group", "agentGroup", "organization", "project",
  "email", "employeeId", "workflow", "role",
]);

const readUserManageTableView = () => {
  const fallback = {
    columnFilters: {},
    visibleCols: DEFAULT_VISIBLE_COLS,
    columnOrder: DEFAULT_COLUMN_ORDER,
    searchTerm: "",
    searchScope: "all",
  };
  try {
    const raw = localStorage.getItem(USER_MANAGE_TABLE_VIEW_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);

    // The search text/scope are session-only: never restored, so opening the screen again
    // (after another menu item, a refresh or a new login) starts with an empty search box.
    const searchTerm = "";
    const searchScope = "all";

    let columnFilters = {};
    if (parsed?.schemaVersion === USER_MANAGE_TABLE_VIEW_SCHEMA_VERSION && parsed?.columnFilters && typeof parsed.columnFilters === "object" && !Array.isArray(parsed.columnFilters)) {
      columnFilters = Object.fromEntries(
        Object.entries(parsed.columnFilters).filter(([key, filter]) => !REMOVED_FILTER_KEYS.has(key) && isMeaningfulColumnFilter(filter))
      );
    }

    // Reconcile against the current default order so a future column addition/
    // removal doesn't silently disappear from — or get stuck out of — a stale
    // saved order: keep only still-known ids (in their saved order), then
    // append any default ids missing from that saved list.
    let columnOrder = DEFAULT_COLUMN_ORDER;
    if (Array.isArray(parsed?.columnOrder)) {
      const known = parsed.columnOrder.filter((id) => DEFAULT_COLUMN_ORDER.includes(id));
      const missing = DEFAULT_COLUMN_ORDER.filter((id) => !known.includes(id));
      const merged = [...known, ...missing];
      if (merged.length === DEFAULT_COLUMN_ORDER.length) columnOrder = merged;
    }

    const visibleCols = { ...DEFAULT_VISIBLE_COLS };
    if (parsed?.visibleCols && typeof parsed.visibleCols === "object" && !Array.isArray(parsed.visibleCols)) {
      Object.keys(DEFAULT_VISIBLE_COLS).forEach((key) => {
        if (typeof parsed.visibleCols[key] === "boolean") visibleCols[key] = parsed.visibleCols[key];
      });
    }
    // Defensive floor matching the live Columns picker's own minimum.
    if (columnOrder.filter((id) => visibleCols[id]).length < MIN_VISIBLE_COLUMNS) {
      columnOrder.slice(0, MIN_VISIBLE_COLUMNS).forEach((id) => {
        visibleCols[id] = true;
      });
    }

    return { columnFilters, visibleCols, columnOrder, searchTerm, searchScope };
  } catch (error) {
    console.error("Error restoring saved user table view:", error);
    return fallback;
  }
};

const UserList = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [userList, setUerList] = useState([]); // current page only — paging/sort/filter run server-side
  const [totalUsers, setTotalUsers] = useState(0);
  // Search text/scope, filters, visible columns, and column order are all
  // hydrated once, synchronously, from localStorage (see
  // readUserManageTableView above) — same lazy-ref pattern TaskListView.js
  // uses for its persisted column layout.
  const initialTableViewRef = useRef(null);
  if (initialTableViewRef.current === null) {
    initialTableViewRef.current = readUserManageTableView();
  }
  const [searchTerm, setSearchTerm] = useState(() => initialTableViewRef.current.searchTerm);
  const [searchScope, setSearchScope] = useState(() => initialTableViewRef.current.searchScope); // see USER_SEARCH_SCOPES
  // Record<apiKey, ColumnFilter> — the task-list grid's filter model.
  const [columnFilters, setColumnFilters] = useState(() => initialTableViewRef.current.columnFilters);
  // Search text actually sent to the server, debounced like the task grid's text filters.
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(() => initialTableViewRef.current.searchTerm);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), TEXT_FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchTerm]);
  const [openFilterPopoverColId, setOpenFilterPopoverColId] = useState(null);
  // Plain object (not per-column useRef, since columns are rendered from a
  // .map()) tracking each funnel button's DOM node so ColumnFilterPopover can
  // position itself off whichever column's filter is currently open.
  const filterButtonRefs = useRef({});
  const [openStatusMenuId, setOpenStatusMenuId] = useState(null);
  const statusMenuRef = useRef(null);
  useEffect(() => {
    if (!openStatusMenuId) return undefined;
    const closeOnOutsideClick = (event) => {
      if (statusMenuRef.current?.contains(event.target)) return;
      setOpenStatusMenuId(null);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpenStatusMenuId(null);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [openStatusMenuId]);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [showUserFormModal, setShowUserFormModal] = useState(false);
  const [modalUserId, setModalUserId] = useState(null);
  const [visibleCols, setVisibleCols] = useState(() => initialTableViewRef.current.visibleCols);
  const [showColPicker, setShowColPicker] = useState(false);
  const columnsButtonRef = useRef(null);
  const [showColumnFilters, setShowColumnFilters] = useState(true);
  // Draws attention to the just-revealed per-column funnel filter icons,
  // mirroring TaskListView.js's / StockManagement.js's "justRevealedFilters" pulse.
  const [justRevealedColumnFilters, setJustRevealedColumnFilters] = useState(false);
  const prevShowColumnFiltersRef = useRef(showColumnFilters);
  useEffect(() => {
    if (showColumnFilters && !prevShowColumnFiltersRef.current) {
      setJustRevealedColumnFilters(true);
      const t = setTimeout(() => setJustRevealedColumnFilters(false), 900);
      prevShowColumnFiltersRef.current = showColumnFilters;
      return () => clearTimeout(t);
    }
    prevShowColumnFiltersRef.current = showColumnFilters;
  }, [showColumnFilters]);
  const [toolbarResetKey, setToolbarResetKey] = useState(0);
  const [columnOrder, setColumnOrder] = useState(() => initialTableViewRef.current.columnOrder);
  const [dragColId, setDragColId] = useState(null);
  const [dragOverColId, setDragOverColId] = useState(null);
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const { theme, selectedOrgId, langMode, userType, projectId } = useContext(AppContext);
  const { isRTL: isRtl, dir } = useDirection();
  const cellAlignClass = isRtl ? "text-right" : "text-left";
  const getColAlignClass = (colId) => (CENTERED_COLUMN_IDS.has(colId) ? "text-center" : cellAlignClass);
  const storedOrganizationId = localStorage.getItem("selectedOrgId");
  const storedProjectId = localStorage.getItem("projectId");
  const organizationId = selectedOrgId || (storedOrganizationId ? JSON.parse(storedOrganizationId) : "");
  // projectIds is required by get-users. Use the nav's selected project; when
  // none is selected (e.g. no project in the nav for this admin/office user),
  // fall back to the projects assigned to the logged-in user.
  const assignedProjectIds = useMemo(() => {
    try {
      const list = JSON.parse(localStorage.getItem("userDetails") || "{}")?.projectList;
      return Array.isArray(list) ? list.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0) : [];
    } catch {
      return [];
    }
  }, []);
  const selectedProjectId = projectId || (storedProjectId && storedProjectId !== "null" && storedProjectId !== "undefined" ? storedProjectId : "");
  const effectiveProjectId = selectedProjectId || assignedProjectIds.join(",");
  // The backend ignores organizationId for everyone but super admins, so only
  // they have to wait for an organization to be selected.
  const isSuperAdminUser = userType === "super_admin";
  const missingGridScope = !effectiveProjectId || (isSuperAdminUser && !organizationId);
  const userPerm = getPermissionByScreenCode("USER_MANAGEMENT");
  const canUpdateUser = userType === "super_admin" || Boolean(userPerm?.canUpdate);

  // Persist filters, visible columns, and column order back to localStorage
  // whenever any of them change, so they survive a refresh, a new tab/window,
  // or logging back in later (search text/scope are session-only). Only
  // resetTableView() (the toolbar Reset button) wipes this storage entry.
  useEffect(() => {
    const persistTimer = window.setTimeout(() => {
      try {
        localStorage.setItem(
          USER_MANAGE_TABLE_VIEW_KEY,
          JSON.stringify({
            schemaVersion: USER_MANAGE_TABLE_VIEW_SCHEMA_VERSION,
            columnFilters,
            visibleCols,
            columnOrder,
          })
        );
      } catch (error) {
        console.error("Error saving user table view:", error);
      }
    }, 150);
    return () => window.clearTimeout(persistTimer);
  }, [columnFilters, visibleCols, columnOrder]);

  // GET /get-users/columns — which columns this user type may see
  // (accessible) and filter (filterable). null until loaded or on failure, in
  // which case the grid falls back to its built-in defaults.
  const [gridColumns, setGridColumns] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchUserGridColumns()
      .then((cols) => {
        if (!cancelled) setGridColumns(cols.length ? cols : null);
      })
      .catch((error) => {
        console.error("Error fetching user grid columns:", error);
        if (!cancelled) setGridColumns(null);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, effectiveProjectId]);
  const columnMetaByKey = useMemo(
    () => (gridColumns ? Object.fromEntries(gridColumns.filter((c) => c?.key).map((c) => [c.key, c])) : null),
    [gridColumns]
  );

  // Toolbar search: "All Fields" goes out as body.search (name, email, phone,
  // employee id); a specific field becomes a `contains` filter on that column.
  const serializedColumnFilters = useMemo(() => serializeColumnFilters(columnFilters), [columnFilters]);
  const trimmedSearch = debouncedSearchTerm.trim();
  const searchScopeApiKey = USER_SEARCH_SCOPES.find((s) => s.value === searchScope)?.apiKey || null;
  const requestSearch = searchScopeApiKey ? "" : trimmedSearch;
  const requestColumnFilters = useMemo(
    () =>
      trimmedSearch && searchScopeApiKey
        ? [...serializedColumnFilters, makeContainsFilter(searchScopeApiKey, trimmedSearch)]
        : serializedColumnFilters,
    [serializedColumnFilters, trimmedSearch, searchScopeApiKey]
  );

  // POST /get-users — one page, filtered and sorted server-side.
  // totalNumberOfUsers is the filtered count. A newer request aborts an older
  // one so a slow response can never overwrite a newer page.
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    if (missingGridScope) {
      if (!effectiveProjectId) console.warn("User list: no project selected or assigned, so get-users was not called (projectIds is required).");
      setUerList([]);
      setTotalUsers(0);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    const backendColumnFilters = (requestColumnFilters || []).filter(
      (f) => f.column !== "organization"
    );
    searchUsers({
      page: currentPage,
      size: pageSize,
      projectIds: effectiveProjectId,
      organizationId,
      search: requestSearch,
      sortBy: sortKey || undefined,
      sortOrder: sortDir,
      columnFilters: backendColumnFilters,
      signal: controller.signal,
    })
      .then(({ userList: rows, totalNumberOfUsers }) => {
        if (controller.signal.aborted) return;
        setUerList(rows);
        setTotalUsers(totalNumberOfUsers);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        // Validation errors (unknown column/operator, bad number, missing
        // projectIds) carry the backend's own message — show it as-is.
        toast.error(error instanceof UserGridError ? error.message : translations[langMode].failedtofetchusers);
        setUerList([]);
        setTotalUsers(0);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingGridScope, organizationId, effectiveProjectId, currentPage, pageSize, requestSearch, sortKey, sortDir, requestColumnFilters, reloadKey]);

  // Rows have no lastLogin field: loginHistory[0] is the most recent entry.
  // Same format as the task list's dates (e.g. "Oct 31, 2026, 2:05 PM");
  // "-" when the user has never logged in.
  const lastLoginDate = (loginHistory) => {
    if (!Array.isArray(loginHistory) || loginHistory.length === 0) return "-";
    const ts = getTimestamp(loginHistory[0]?.loginAt);
    if (!ts) return "-";
    return new Date(ts).toLocaleString("en-US", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "numeric", hour12: true });
  };

  const [loading, setLoading] = useState(true);
  const [showLoginHistoryModal, setShowLoginHistoryModal] = useState(false);
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);
  const [loginHistoryError, setLoginHistoryError] = useState("");
  const [selectedUserName, setSelectedUserName] = useState("");
  const [selectedUserLoginHistory, setSelectedUserLoginHistory] = useState([]);
  const [resolvedLocations, setResolvedLocations] = useState({});
  const locationRequestCounter = useRef(0);
  const locale = useMemo(() => {
    if (langMode === "ar") return "ar";
    if (langMode === "ur") return "ur-PK";
    return "en-US";
  }, [langMode]);
  const dateTimeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
    [locale]
  );
  const translatedNA = translations[langMode].notAvailable || "N/A";

  const formatLoginDate = (dateString) => {
    if (!dateString) return "-";
    const normalized = String(dateString)
      .replace(" ", "T")
      .replace(/(\.\d{3})\d+/, "$1");
    const parsedDate = new Date(normalized);
    if (Number.isNaN(parsedDate.getTime())) return String(dateString);
    return dateTimeFormatter.format(parsedDate);
  };

  const getTimestamp = (dateString) => {
    if (!dateString) return 0;
    const normalized = String(dateString)
      .replace(" ", "T")
      .replace(/(\.\d{3})\d+/, "$1");
    const parsedDate = new Date(normalized);
    return Number.isNaN(parsedDate.getTime()) ? 0 : parsedDate.getTime();
  };

  const formatCoordinates = (latitude, longitude) => {
    if (latitude == null || longitude == null) {
      return translations[langMode].locationUnavailable || "Location unavailable";
    }
    return `Lat: ${Number(latitude).toFixed(6)}, Lng: ${Number(longitude).toFixed(6)}`;
  };

  const getMapUrl = (latitude, longitude) => {
    if (latitude == null || longitude == null) return "";
    return `https://www.google.com/maps?q=${latitude},${longitude}`;
  };

  const getLocationKey = (latitude, longitude) => `${latitude},${longitude}`;

  const getLocationLabel = async (latitude, longitude) => {
    if (latitude == null || longitude == null) {
      return translations[langMode].locationUnavailable || "Location unavailable";
    }
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`
      );
      if (!response.ok) {
        throw new Error("Reverse geocode failed");
      }
      const data = await response.json();
      return data?.display_name || formatCoordinates(latitude, longitude);
    } catch (error) {
      return formatCoordinates(latitude, longitude);
    }
  };

  const openLoginHistoryModal = async (userId, userName) => {
    const requestId = locationRequestCounter.current + 1;
    locationRequestCounter.current = requestId;
    setShowLoginHistoryModal(true);
    setLoginHistoryLoading(true);
    setLoginHistoryError("");
    setSelectedUserName(userName || "");
    setSelectedUserLoginHistory([]);
    setResolvedLocations({});

    try {
      const response = await getUserInfoAPI(userId);
      const apiValue = response?.data?.body?.value;
      const userDetails =
        apiValue?.userDetails ||
        apiValue ||
        response?.data?.userDetails ||
        response?.data ||
        {};

      const historyCandidates = [
        userDetails?.loginHistory,
        apiValue?.loginHistory,
        apiValue?.userDetails?.loginHistory,
        response?.data?.body?.loginHistory,
      ];
      const history =
        historyCandidates.find((item) => Array.isArray(item)) || [];

      const sortedHistory = [...history].sort(
        (a, b) => getTimestamp(b?.loginAt) - getTimestamp(a?.loginAt)
      );

      setSelectedUserLoginHistory(sortedHistory);

      const uniqueCoordinates = [...new Set(
        sortedHistory
          .filter((entry) => entry?.latitude != null && entry?.longitude != null)
          .map((entry) => getLocationKey(entry.latitude, entry.longitude))
      )];

      if (uniqueCoordinates.length > 0) {
        const locationMap = {};
        await Promise.all(
          uniqueCoordinates.map(async (key) => {
            const [latitude, longitude] = key.split(",");
            locationMap[key] = await getLocationLabel(latitude, longitude);
          })
        );
        if (locationRequestCounter.current === requestId) {
          setResolvedLocations(locationMap);
        }
      }
    } catch (error) {
      setLoginHistoryError(
        translations[langMode].failedToFetchLoginHistory || "Failed to fetch login history."
      );
    } finally {
      setLoginHistoryLoading(false);
    }
  };

  const closeLoginHistoryModal = () => {
    setShowLoginHistoryModal(false);
    setLoginHistoryLoading(false);
    setLoginHistoryError("");
    setSelectedUserName("");
    setSelectedUserLoginHistory([]);
    setResolvedLocations({});
    locationRequestCounter.current += 1;
  };

  useEffect(() => {
    if (!showLoginHistoryModal) return undefined;
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeLoginHistoryModal();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [showLoginHistoryModal]);

  const editInfo = (userId) => {
    setModalUserId(userId);
    setShowUserFormModal(true);
  };

  const openAddUserModal = () => {
    setModalUserId(null);
    setShowUserFormModal(true);
  };

  const closeUserFormModal = () => {
    setShowUserFormModal(false);
    setModalUserId(null);
  };

  const handleUserFormSuccess = () => {
    closeUserFormModal();
    setReloadKey((k) => k + 1);
  };

  // Search runs server-side (see the POST /get-users effect above); the
  // toolbar only updates state, and the request goes out debounced.
  const handleSearch = (term) => {
    setSearchTerm(term);
  };
  const handleSearchScopeChange = (scope) => {
    setSearchScope(scope);
  };

  // Matches the prototype's sizing model: only User has an explicit fixed width
  // (240px); every other column — including Action — sizes itself from its own
  // content. Pixel minimums act only as a legibility floor on narrow screens.
  // maxWidth is a hard ceiling so one long nowrap value can't blow a column out.
  //
  // apiKey ties each column to GET /get-users/columns (accessible/filterable),
  // the columnFilters column key and sortBy. Filters: a value checklist (op
  // "in") by default, or a from/to date range (op "between", yyyy-MM-dd) for
  // Last Login. The details columns (Department/Project/Workflows/Roles and
  // both group columns) filter by *name* on their *Details key.
  //
  // Labels always come from here: /columns returns the raw key as the label
  // for the details columns ("groupDetails", "projectDetails", …).
  const includeStatusColumn = userType === "super_admin" || userType === "admin";
  const t = translations[langMode] || {};
  const ALL_COLUMN_DEFS = [
    { id: "userId", label: t.user || "User", minWidth: 240, apiKey: "name" },
    { id: "userType", label: t.userType, minWidth: 140, maxWidth: 200, apiKey: "userType" },
    { id: "phone", label: t.Phone, minWidth: 170, maxWidth: 200, apiKey: "phone" },
    { id: "status", label: t.Status, minWidth: 150, maxWidth: 200, apiKey: "userStatus" },
    { id: "organization", label: t.org || "Organization", minWidth: 150, maxWidth: 220, apiKey: "organization" },
    { id: "department", label: t.department || "Department", minWidth: 180, maxWidth: 260, apiKey: "departmentDetails" },
    { id: "project", label: t.project || "Project", minWidth: 180, maxWidth: 260, apiKey: "projectDetails" },
    { id: "group", label: t.assignedGroups || "Assigned Groups", minWidth: 170, maxWidth: 240, apiKey: "groupListDetails" },
    { id: "agentGroup", label: t.agentGroup || "Agent Group", minWidth: 150, maxWidth: 220, apiKey: "groupDetails" },
    { id: "lastLogin", label: t.lastLogin || "Last Login", minWidth: 150, maxWidth: 190, apiKey: "lastLogin", rangeType: "date" },
    { id: "email", label: t.email || "Email", minWidth: 200, maxWidth: 280, apiKey: "email" },
    { id: "employeeId", label: t.userColEmployeeId || "Employee ID", minWidth: 140, maxWidth: 200, apiKey: "employeeId" },
    { id: "workflow", label: t.userColWorkflows || "Workflows", minWidth: 180, maxWidth: 260, apiKey: "workflowDetails" },
    { id: "role", label: t.userColRoles || t.roles || "Roles", minWidth: 180, maxWidth: 260, apiKey: "roleDetails" },
    { id: "action", label: t.Action, minWidth: 90 },
  ].map((def) => {
    const meta = def.apiKey && columnMetaByKey ? columnMetaByKey[def.apiKey] : undefined;
    const filterKey = def.apiKey || null;
    // Filter icon only where the columns API says filterable: true (before it
    // loads, everything but organization). Columns marked accessible:false
    // are hidden.
    const filterable = Boolean(filterKey) && (
      def.id === "organization"
        ? true
        : meta
          ? meta.filterable === true
          : !NON_FILTERABLE_API_KEYS.has(filterKey)
    );
    const accessible = def.id === "userId" || def.id === "action"
      ? true
      : meta
        ? meta.accessible !== false
        : !METADATA_ONLY_COLUMN_IDS.has(def.id);
    return {
      ...def,
      label: def.label || def.id,
      filterKey,
      filterable,
      accessible,
      sortKey: def.apiKey && SORTABLE_API_KEYS.has(def.apiKey) ? def.apiKey : null,
    };
  });
  const defsById = Object.fromEntries(ALL_COLUMN_DEFS.map((c) => [c.id, c]));
  // userId and Action are pinned; the rest are toggleable via the Columns
  // picker, gated by role (status) and by the columns API (accessible).
  const isColumnAvailable = (id) => Boolean(defsById[id]?.accessible) && (id !== "status" || includeStatusColumn);
  const columnVisibility = {
    userId: true,
    ...Object.fromEntries(DEFAULT_COLUMN_ORDER.map((id) => [id, isColumnAvailable(id) && Boolean(visibleCols[id])])),
    action: canUpdateUser,
  };
  const toggleableColumns = columnOrder.filter(isColumnAvailable).map((id) => defsById[id]);
  const orderedVisibleToggleable = columnOrder.filter((id) => columnVisibility[id]);
  const visibleToggleableCount = toggleableColumns.filter((c) => visibleCols[c.id]).length;
  // Shape/keys expected by the shared ColumnsCustomizeModal (Task Management's
  // Columns picker) — kept distinct from tableHeaders below, which is the
  // actual rendered table column list.
  const columnsModalHeaders = toggleableColumns.map((c) => ({ value: c.id, label: c.label }));
  const columnsModalVisibleKeys = toggleableColumns.filter((c) => visibleCols[c.id]).map((c) => c.id);
  const columnsModalDefaultKeys = toggleableColumns.filter((c) => DEFAULT_VISIBLE_COLS[c.id]).map((c) => c.id);
  const handleApplyColumnsModal = (nextKeys = []) => {
    // Merge rather than replace: toggleableColumns omits columns this role
    // can't see, so a full replace would silently drop their saved choice.
    setVisibleCols((current) => ({
      ...current,
      ...Object.fromEntries(toggleableColumns.map((c) => [c.id, nextKeys.includes(c.id)])),
    }));
  };
  const tableHeaders = [
    defsById.userId,
    ...orderedVisibleToggleable.map((id) => defsById[id]),
    ...(columnVisibility.action ? [defsById.action] : []),
  ];
  // User and Action are pinned to a plain fixed pixel width so the auto-layout
  // algorithm never inflates them with leftover space.
  const FIXED_COL_WIDTH = { userId: 240, action: 100 };
  const tableMinWidthPx = tableHeaders.reduce((sum, c) => sum + c.minWidth, 0);

  // Display labels for picker values. The raw value is what gets sent back:
  // userType is the enum name (field_agent), userStatus is "true"/"false".
  const formatUserTypeValue = (value) =>
    t[value] ?? String(value).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const formatUserStatusValue = (value) => {
    const v = String(value).toLowerCase();
    if (v === "true") return String(t.active || "Active").trim();
    if (v === "false") return String(t.Inactive || "Inactive").trim();
    return String(value);
  };
  const FILTER_VALUE_FORMATTERS = { userType: formatUserTypeValue, userStatus: formatUserStatusValue };

  // Writes one column's filter; an empty/meaningless filter removes the entry
  // (empty picker = "don't filter this column", never "match nothing") rather
  // than sending an empty filter. The backend accepts at most
  // MAX_COLUMN_FILTERS, including the toolbar's field-scoped search.
  const searchFilterSlots = trimmedSearch && searchScopeApiKey ? 1 : 0;
  const setColumnFilterEntry = (filterKey, nextFilter) => {
    const adding = nextFilter && isMeaningfulColumnFilter(nextFilter);
    if (adding && !columnFilters[filterKey] && countActiveColumnFilters(columnFilters) + searchFilterSlots >= MAX_COLUMN_FILTERS) {
      toast.error(t.userFilterLimitReached || `You can apply at most ${MAX_COLUMN_FILTERS} column filters at once. Remove one to add another.`);
      return;
    }
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (adding) next[filterKey] = nextFilter;
      else delete next[filterKey];
      return next;
    });
  };
  // Stable so the popover's outside-click/Escape listeners aren't re-bound every render.
  const closeFilterPopover = useCallback(() => setOpenFilterPopoverColId(null), []);
  const isColumnFiltered = (col) => Boolean(col.filterKey && isMeaningfulColumnFilter(columnFilters[col.filterKey]));

  // Funnel content: the shared task-list picker, fed by
  // POST /get-users/column-values with this column, the dropdown's search
  // text (to look past the 200-value cap), the same project scope as the grid
  // and the *other* columns' active filters. "" comes back as "(Blanks)" and
  // is sent back as "".
  const renderColumnFilter = (col, closePopover) => {
    const otherColumnFilters = requestColumnFilters.filter((f) => f.column !== col.filterKey);
    const stored = columnFilters[col.filterKey];
    if (col.rangeType === "date") {
      return (
        <DateRangeColumnFilter
          from={stored?.op === "between" ? stored.from : ""}
          to={stored?.op === "between" ? stored.to : ""}
          onApply={(from, to) => {
            setColumnFilterEntry(col.filterKey, makeBetweenFilter(col.filterKey, from, to));
            closePopover();
          }}
        />
      );
    }
    return (
      <ColumnValueFilterList
        column={col.filterKey}
        loadValues={async (query) => {
          if (col.filterKey === "organization") {
            try {
              const res = await fetchUserColumnValues({
                projectIds: effectiveProjectId,
                organizationId,
                column: col.filterKey,
                search: query,
                columnFilters: otherColumnFilters,
              });
              if (Array.isArray(res) && res.length) return res;
            } catch {
              // Backend column-values does not support organization; fallback to loaded user records
            }
            const q = String(query ?? "").trim().toLowerCase();
            const names = Array.from(
              new Set(
                (userList || [])
                  .map((u) => u.organization?.organizationName)
                  .filter(Boolean)
              )
            );
            return q ? names.filter((n) => n.toLowerCase().includes(q)) : names;
          }
          return fetchUserColumnValues({
            projectIds: effectiveProjectId,
            organizationId,
            column: col.filterKey,
            search: query,
            columnFilters: otherColumnFilters,
          });
        }}
        columnConditions={otherColumnFilters}
        formatLabel={FILTER_VALUE_FORMATTERS[col.filterKey]}
        containsHelpText={t.userContainsHelp || "Case-insensitive match across all users."}
        selected={stored?.op === "in" ? stored.values : []}
        containsValue={stored?.op === "contains" ? stored.value : ""}
        onApply={(values) => {
          setColumnFilterEntry(col.filterKey, values.length ? makeInFilter(col.filterKey, values) : null);
          closePopover();
        }}
        onApplyContains={(value) => {
          setColumnFilterEntry(col.filterKey, String(value ?? "").trim() ? makeContainsFilter(col.filterKey, value) : null);
          closePopover();
        }}
      />
    );
  };

  // Header click: first click sorts ascending, the next toggles asc/desc.
  // Sorting covers all filtered users server-side; the page resets to 0.
  const toggleSort = (key) => {
    if (!key) return;
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const pageRows = useMemo(() => {
    const orgFilter = columnFilters["organization"];
    if (!orgFilter) return userList;
    return (userList || []).filter((user) => {
      const orgName = (user.organization?.organizationName || "").toLowerCase();
      if (orgFilter.op === "in") {
        const allowed = (orgFilter.values || []).map((v) => String(v).toLowerCase());
        return allowed.includes(orgName);
      }
      if (orgFilter.op === "contains") {
        return orgName.includes(String(orgFilter.value || "").toLowerCase());
      }
      return true;
    });
  }, [userList, columnFilters]);

  const totalItems = columnFilters["organization"] ? pageRows.length : totalUsers;
  const totalPages = Math.max(0, Math.ceil(totalItems / pageSize) - 1);

  // Any change to what is being asked for starts again from the first page.
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm, searchScope, columnFilters, sortKey, sortDir]);

  const activeColumnFilterCount = countActiveColumnFilters(columnFilters);

  const isDefaultTableView = !searchTerm &&
    columnOrder.join() === DEFAULT_COLUMN_ORDER.join() &&
    !sortKey && searchScope === "all" && showColumnFilters &&
    activeColumnFilterCount === 0 &&
    toggleableColumns.every((col) => visibleCols[col.id] === DEFAULT_VISIBLE_COLS[col.id]);

  const handleColumnDrop = (targetId) => {
    if (dragColId && dragColId !== targetId) {
      setColumnOrder((order) => {
        const next = [...order];
        const from = next.indexOf(dragColId);
        const to = next.indexOf(targetId);
        if (from === -1 || to === -1) return order;
        next.splice(from, 1);
        next.splice(to, 0, dragColId);
        return next;
      });
    }
    setDragColId(null);
    setDragOverColId(null);
  };

  const resetTableView = () => {
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setSearchScope("all");
    setColumnFilters({});
    setOpenFilterPopoverColId(null);
    setSortKey(null);
    setSortDir("asc");
    setShowColumnFilters(true);
    setVisibleCols(DEFAULT_VISIBLE_COLS);
    setColumnOrder(DEFAULT_COLUMN_ORDER);
    setCurrentPage(0);
    setToolbarResetKey((k) => k + 1);
    try {
      localStorage.removeItem(USER_MANAGE_TABLE_VIEW_KEY);
    } catch (error) {
      console.error("Error clearing saved user table view:", error);
    }
  };

  const hasActiveFilters = Boolean(searchTerm.trim()) || activeColumnFilterCount > 0 || Boolean(sortKey);
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + activeColumnFilterCount + (sortKey ? 1 : 0);

  // Unused while the CSV download button is commented out (see toolbar below).
  // eslint-disable-next-line no-unused-vars
  const exportCsv = () => {
    if (!pageRows || pageRows.length === 0) return;
    // Name/email now live inside the pinned identity cell rather than as toggleable
    // columns, but are still exported unconditionally alongside whatever's visible.
    // Exports only the current page of the filtered/sorted view, matching prior behavior.
    const identityColumns = [
      { id: "userId", label: translations[langMode].UserId },
      { id: "name", label: translations[langMode].username || "Name" },
      { id: "userEmail", label: translations[langMode].Email || "Email" },
    ];
    const exportableColumns = [...identityColumns, ...tableHeaders.filter((c) => c.id !== "action" && c.id !== "userId")];
    const escapeCsv = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
    const headerRow = exportableColumns.map((c) => escapeCsv(c.label)).join(",");
    const dataRows = pageRows.map((u) =>
      exportableColumns
        .map((c) => {
          if (c.id === "userId") return escapeCsv(u.userId);
          if (c.id === "name") return escapeCsv(u.name);
          if (c.id === "userEmail") return escapeCsv(u.email);
          if (c.id === "userType") return escapeCsv(u.userType);
          if (c.id === "username") return escapeCsv(u.name);
          if (c.id === "email") return escapeCsv(u.email);
          if (c.id === "phone") return escapeCsv(u.phone);
          if (c.id === "status") return escapeCsv(u.userStatus ? "Enable" : "Disable");
          if (c.id === "organization") return escapeCsv(u.organization?.organizationName || "");
          if (c.id === "department") return escapeCsv(detailNames(u.departmentDetails).join("; "));
          if (c.id === "project") return escapeCsv(detailNames(u.projectDetails).join("; "));
          if (c.id === "workflow") return escapeCsv(detailNames(u.workflowDetails).join("; "));
          if (c.id === "role") return escapeCsv(detailNames(u.roleDetails).join("; "));
          if (c.id === "group") return escapeCsv(assignedGroupsOf(u).map((g) => g?.groupName || "#" + g?.groupId).join("; "));
          if (c.id === "agentGroup") return escapeCsv(agentGroupsOf(u).map((g) => g?.groupName || "#" + g?.groupId).join("; "));
          if (c.id === "employeeId") return escapeCsv(u[c.id]);
          if (c.id === "lastLogin") return escapeCsv(lastLoginDate(u.loginHistory));
          return escapeCsv("");
        })
        .join(",")
    );
    const csvContent = [headerRow, ...dataRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `users-page-${currentPage + 1}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const rowH = "py-2.5";

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };


  // The backend scopes get-users to what each user type is allowed to see, so
  // every page POST /get-users returns is already correctly scoped.

  const handleActionChange = async (selectedAction, userId) => {
    const updatedStatus = selectedAction === "Enable" ? true : false;
    try {
      const success = await updateUserStatus(userId, updatedStatus);
      if (success) {
        const updatedUserData = userList.map((user) =>
          user.userId === userId ? { ...user, userStatus: updatedStatus } : user
        );
        setUerList(updatedUserData);
        toast.success(translations[langMode].userStatusupdatesuc);
      } else {
        throw new Error("Failed to update user status");
      }
    } catch (error) {
      toast.error(error?.response?.data?.body?.value || error?.body?.value || translations[langMode].failedtoupdateuserstatus);
    }
  };

  return (
    <>
      <div
        className={`flex h-full w-full flex-col overflow-hidden ${theme === "dark"
          ? "dark-mode"
          : theme === "high-contrast"
            ? "high-contrast"
            : ""
          }`}
      >
        <div
          className={`shrink-0 flex flex-col gap-3 mx-2 mt-2 rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm sm:mx-4 sm:mt-3 sm:gap-4 sm:px-5 sm:py-4 ${theme === "dark"
            ? "dark-mode "
            : theme === "high-contrast"
              ? "high-contrast  "
              : ""
            }`}
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <h1
                className="text-base font-bold text-slate-800 leading-tight sm:text-lg inline-flex items-center gap-2"
                data-testid="user-list-title"
              >
                {translations[langMode].userlist}
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                {translations[langMode]?.userManagementSubtitle ?? "Add, edit, and manage user access across your organization."}
              </p>
            </div>
            {hasScreenAccess("USER_MANAGEMENT", "canCreate") && (
              <CommonTooltip
                text={translations[langMode].addauserassingroles}
                position="left"
                disableDefaultDescription
                tooltipClassName="w-max max-w-[min(15rem,calc(100vw-1.5rem))] [text-wrap:balance]"
                fitContent
                clampToViewport
                hoverOnly
              >
                <CommonButton colorIntent="primary"
                  onClick={openAddUserModal}
                  variant="primary"
                  data-testid="add-user"
                >
                  <UserPlus className="w-5 h-5 me-1" />
                  <span className="hidden sm:inline">
                    {translations[langMode].addUser}
                  </span>
                </CommonButton>
              </CommonTooltip>
            )}
          </div>

          <div className="flex min-w-0 flex-wrap items-center justify-between gap-3">
            <div className="flex w-full min-w-0 flex-wrap items-center gap-2 lg:w-auto">
              <HoverTooltip text={translations[langMode]?.totalUsersTooltip || "Total number of users matching the current search and filters."}>
                <div className="h-9 px-3 rounded-lg border border-slate-200 flex items-center gap-1.5 text-sm  shrink-0 text-slate-700">
                  {loading ? "—" : totalItems} {translations[langMode].users || "users"}
                </div>
              </HoverTooltip>
              <HoverTooltip text={translations[langMode]?.filtersToggleTooltip || "Apply filters from the table header"}>
                <button data-table-control="filters" aria-pressed={showColumnFilters}
                  type="button"
                  onClick={() => setShowColumnFilters((v) => !v)}
                  className="h-9 px-3 rounded-lg border text-sm font-medium flex items-center gap-2 shrink-0 bg-white"
                  style={{
                    borderColor: showColumnFilters ? "#93C5FD" : "#E2E8F0",
                    color: showColumnFilters ? "#1D4ED8" : "#334155",
                  }}
                >
                  <Filter size={14} /> {translations[langMode]?.columnFiltersLabel ?? "Column Filters"}
                </button>
              </HoverTooltip>
              <UserToolbar
                key={toolbarResetKey}
                initialSearchTerm={searchTerm}
                initialSearchScope={searchScope}
                onSearch={handleSearch}
                onSearchScopeChange={handleSearchScopeChange}
                scopes={USER_SEARCH_SCOPES}
              />
            </div>

            <div className="flex w-full min-w-0 flex-wrap items-center justify-between gap-2 sm:w-auto sm:justify-start">
              <div className="relative shrink-0 h-8 flex items-center">
                <HoverTooltip text={translations[langMode]?.resetAllAppliedFiltersTooltip || "Clear all applied filters"}>
                  <button data-button-intent="warning"
                    type="button"
                    onClick={resetTableView}
                    disabled={isDefaultTableView}
                    className="h-8 px-2.5 rounded-lg border border-slate-200 text-xs font-medium text-slate-600 flex items-center gap-1.5 disabled:opacity-40 hover:bg-slate-50"
                  >
                    <RotateCcw size={13} /> <span className="hidden md:inline">{translations[langMode].reset || "Reset"}</span>
                  </button>
                </HoverTooltip>
                {activeFilterCount > 0 && (
                  <span
                    className={`absolute -top-2 ${isRtl ? "-left-2" : "-right-2"} grid min-w-[20px] h-5 px-1 place-items-center rounded-full bg-[#ff3b30] text-white text-[11px] font-semibold leading-[20px] tabular-nums pointer-events-none`}
                    aria-label={`${activeFilterCount} ${translations[langMode]?.appliedFiltersAria || "applied filters"}`}
                  >
                    {activeFilterCount > 99 ? "99+" : activeFilterCount}
                  </span>
                )}
              </div>

              <div ref={columnsButtonRef} className="relative h-8 flex items-center">
                <HoverTooltip text={translations[langMode]?.columnsTooltip || "Choose which columns appear in the table."}>
                  <ColumnsButton
                    type="button"
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={() => setShowColPicker((v) => !v)}
                    className="h-8 px-2.5 rounded-lg border text-xs font-medium flex items-center gap-1.5"

                    aria-expanded={showColPicker}
                  >
                    <Columns3 size={13} />
                    <span className="hidden md:inline">{translations[langMode].columns || "Columns"}</span>
                    <span className="inline-flex h-5 min-w-[34px] items-center justify-center rounded-full bg-slate-100 px-1.5 text-[11px] leading-none tabular-nums text-slate-500">
                      {visibleToggleableCount}/{toggleableColumns.length}
                    </span>
                  </ColumnsButton>
                </HoverTooltip>
                <ColumnsCustomizeModal
                  open={showColPicker}
                  onClose={() => setShowColPicker(false)}
                  tableHeaders={columnsModalHeaders}
                  visibleColumnKeys={columnsModalVisibleKeys}
                  defaultSelectedKeys={columnsModalDefaultKeys}
                  minSelected={MIN_VISIBLE_COLUMNS}
                  onApply={handleApplyColumnsModal}
                  langMode={langMode}
                  anchorRef={columnsButtonRef}
                />
              </div>

              {/* CSV download hidden for now. To restore: uncomment this block and
                  re-add `Download` to the lucide-react import.
              <HoverTooltip text={translations[langMode]?.exportCsvTooltip || "Export the current page as a CSV file."}>
                <button
                  type="button"
                  onClick={exportCsv}
                  disabled={!pageRows || pageRows.length === 0}
                  className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-600 hover:bg-slate-50 disabled:opacity-40"
                >
                  <Download size={13} />
                </button>
              </HoverTooltip>
              */}
            </div>
          </div>
        </div>

        <div
          className={`${theme === "dark"
            ? "dark-mode"
            : theme === "high-contrast"
              ? "high-contrast"
              : ""
            }  flex-1 min-h-0 w-full flex flex-col`}
        >
          <div className="user-management-table mx-2 mb-2 mt-2 flex min-h-[24rem] min-w-0 flex-none flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:mx-4 sm:mb-3 sm:mt-3 sm:min-h-0 sm:flex-1">
            <div className="relative z-20 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-slate-100 bg-white px-3 py-2.5 sm:px-5">
              <p className="flex items-center gap-1.5 text-xs text-slate-500 sm:gap-4">
                <span className="whitespace-nowrap">
                  {`${translations[langMode].showing || "Showing"} ${startIndex + 1}-${Math.min(
                    endIndex,
                    totalItems
                  )} ${translations[langMode].of || "of"} ${totalItems}`}
                </span>
              </p>

              <div className="flex min-w-0 items-center gap-2 text-xs text-slate-500 sm:gap-3">
                <div className="flex items-center gap-1.5">
                  <label htmlFor="pageSize" className="whitespace-nowrap">{translations[langMode].rows}</label>
                  <PageSizeSelector value={pageSize} onChange={handlePageSizeChange} height="32px" fontSize="12px" />
                </div>
                <span className="pager-nav flex items-center gap-1">
                  <PagerArrowButton
                    direction="prev"
                    isRtl={isRtl}
                    onClick={goToPreviousPage}
                    disabled={currentPage === 0}
                  />
                  <span className="text-xs text-slate-600 px-1 whitespace-nowrap">
                    {currentPage + 1} / {Math.max(totalPages + 1, 1)}
                  </span>
                  <PagerArrowButton
                    direction="next"
                    isRtl={isRtl}
                    onClick={goToNextPage}
                    disabled={currentPage === totalPages}
                  />
                </span>
              </div>
            </div>
            {loading && <Spinner />}
            <div className="task-list-scrollbar mobile-hide-scrollbar w-full flex-1 min-h-0 overflow-x-auto overflow-y-auto">
              <table
                className="w-full border-collapse text-xs text-start text-gray-500"
                style={{ minWidth: tableMinWidthPx }}
                dir={dir}
                data-testid="user-list-table"
              >
                <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-xs text-slate-700">
                  <tr>
                    {tableHeaders.map((col) => {
                      const isActionHeader = col.id === "action";
                      const isUserHeader = col.id === "userId";
                      const isPinned = isUserHeader || isActionHeader;
                      const isSortable = Boolean(col.sortKey);
                      const isFilterable = Boolean(col.filterable);
                      const isSortedCol = isSortable && sortKey === col.sortKey;
                      return (
                        <th
                          key={col.id}
                          scope="col"
                          draggable={!isPinned}
                          onDragStart={() => !isPinned && setDragColId(col.id)}
                          onDragOver={(e) => {
                            if (isPinned) return;
                            e.preventDefault();
                            setDragOverColId(col.id);
                          }}
                          onDragLeave={() => setDragOverColId((c) => (c === col.id ? null : c))}
                          onDrop={() => !isPinned && handleColumnDrop(col.id)}
                          onDragEnd={() => { setDragColId(null); setDragOverColId(null); }}
                          style={{
                            width: isPinned ? FIXED_COL_WIDTH[col.id] : undefined,
                            minWidth: isPinned ? FIXED_COL_WIDTH[col.id] : col.minWidth,
                            maxWidth: isPinned ? FIXED_COL_WIDTH[col.id] : col.maxWidth,
                            background: dragOverColId === col.id ? "#EFF6FF" : undefined,
                            opacity: dragColId === col.id ? 0.4 : 1,
                          }}
                          className={`relative px-4 py-3.5 text-xs font-semibold uppercase tracking-[0.04em] text-slate-500 ${getColAlignClass(col.id)} ${!isPinned ? "cursor-grab active:cursor-grabbing select-none" : ""} ${isUserHeader ? `sm:sticky ${isRtl ? "sm:right-0" : "sm:left-0"} bg-slate-50 sm:z-20` : ""}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {!isPinned && <GripVertical size={12} className="opacity-40 shrink-0" />}
                            <span
                              className={isSortable ? "inline-flex items-center gap-1 cursor-pointer" : "inline-flex items-center gap-1"}
                              onClick={() => isSortable && toggleSort(col.sortKey)}
                            >
                              {col.label}
                              {isSortable && (
                                isSortedCol
                                  ? (sortDir === "asc" ? <ChevronUp size={13} /> : <ChevronDown size={13} />)
                                  : <ChevronsUpDown size={13} className="opacity-30" />
                              )}
                            </span>
                            {isFilterable && showColumnFilters && (
                              <span className={justRevealedColumnFilters ? "inline-flex animate-filter-pulse" : "inline-flex"}>
                                <button
                                  type="button"
                                  ref={(el) => { filterButtonRefs.current[col.id] = el; }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenFilterPopoverColId(openFilterPopoverColId === col.id ? null : col.id);
                                  }}
                                  className={`ml-0.5 inline-flex items-center justify-center w-5 h-5 rounded shrink-0 transition-colors ${isColumnFiltered(col) ? "text-[#236DB4] bg-blue-50" : "text-[#236DB4] hover:bg-blue-50"}`}
                                >
                                  <Filter size={12} fill={isColumnFiltered(col) ? "currentColor" : "none"} />
                                </button>
                              </span>
                            )}
                          </span>
                          {isFilterable && showColumnFilters && openFilterPopoverColId === col.id && (
                            <ColumnFilterPopover
                              onClose={closeFilterPopover}
                              anchorEl={filterButtonRefs.current[col.id]}
                              width={col.rangeType === "date" ? DATE_FILTER_POPOVER_WIDTH : undefined}
                            >
                              {renderColumnFilter(col, closeFilterPopover)}
                            </ColumnFilterPopover>
                          )}
                        </th>
                      );
                    })}
                    {/* Unconstrained filler column — absorbs any leftover width when the
                        capped/pinned columns don't reach the container's full width, so the
                        header/row backgrounds reach the scrollbar instead of leaving a blank
                        gap. Collapses to ~0 width on its own when the table needs to scroll. */}
                    <th aria-hidden="true" className="p-0" />
                  </tr>
                </thead>
                <tbody>
                  {loading && Array.from({ length: 6 }).map((_, i) => (
                    <tr key={`skeleton-${i}`} className="border-b border-slate-100">
                      <td
                        className={`${isRtl ? "pr-4 pl-2" : "pl-4 pr-2"} ${rowH} sm:sticky ${isRtl ? "sm:right-0" : "sm:left-0"} bg-white`}
                        style={{ width: 240, minWidth: 240, maxWidth: 240 }}
                      >
                        <div className="flex items-center gap-3 justify-start">
                          <div className="w-8 h-8 rounded-full bg-slate-100 animate-pulse shrink-0" />
                          <div className="space-y-1.5 min-w-0">
                            <div className="h-3 w-28 rounded bg-slate-100 animate-pulse" />
                            <div className="h-2.5 w-36 rounded bg-slate-100 animate-pulse" />
                          </div>
                        </div>
                      </td>
                      {columnOrder.filter((colId) => columnVisibility[colId]).map((colId) => (
                        <td key={colId} className={`px-4 ${rowH}`} style={{ maxWidth: defsById[colId].maxWidth }}>
                          <div className="h-3 w-16 rounded bg-slate-100 animate-pulse" />
                        </td>
                      ))}
                      {columnVisibility.action && (
                        <td
                          className={`px-4 ${rowH}`}
                          style={{ width: FIXED_COL_WIDTH.action, minWidth: FIXED_COL_WIDTH.action, maxWidth: FIXED_COL_WIDTH.action }}
                        />
                      )}
                      <td aria-hidden="true" className="p-0" />
                    </tr>
                  ))}
                  {pageRows && pageRows.length > 0 ? (
                    pageRows.map((user) => (
                      <tr
                        key={user.userId}
                        className="group/row text-xs border-b border-slate-100 transition-colors duration-150 bg-white hover:bg-slate-50/70"
                      >
                        {/* User identity (pinned, sticky) — avatar + name + email · #id */}
                        <td
                          className={`${isRtl ? "pr-4 pl-2" : "pl-4 pr-2"} ${rowH} sm:sticky ${isRtl ? "sm:right-0" : "sm:left-0"} bg-white group-hover/row:bg-slate-50 sm:z-[1] ${cellAlignClass}`}
                          style={{ width: 240, minWidth: 240, maxWidth: 240 }}
                        >
                          {/* justify-start (not justify-end) deliberately — justify-content is
                              writing-direction aware in CSS, so flex-start already resolves to
                              the right edge under the table's inherited dir="rtl"; flex-end
                              would instead push to the left in RTL too, which is the actual
                              bug being fixed here. */}
                          <div className="flex items-center gap-3 min-w-0 justify-start">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0"
                              style={{ background: nameToHslColor(user.name || String(user.userId)) }}
                            >
                              {initials(user.name)}
                            </div>
                            <div className="min-w-0">
                              <OverflowTooltipText text={user.name} className="text-sm font-medium text-slate-800" />
                              <OverflowTooltipText text={`${user.email} · #${user.userId}`} className="text-xs text-slate-500" />
                            </div>
                          </div>
                        </td>
                        {/* Toggleable columns, rendered in the user's drag order */}
                        {columnOrder.filter((colId) => columnVisibility[colId]).map((colId) => {
                          if (colId === "userType") {
                            return (
                              <td key={colId} className={`px-4 ${rowH} whitespace-nowrap text-center`} style={{ maxWidth: defsById.userType.maxWidth }}>
                                <span
                                  className="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-md"
                                  style={{
                                    color: (USER_TYPE_BADGE_STYLES[user.userType] || USER_TYPE_BADGE_STYLES.admin).color,
                                    background: (USER_TYPE_BADGE_STYLES[user.userType] || USER_TYPE_BADGE_STYLES.admin).bg,
                                  }}
                                >
                                  {formatUserTypeValue(user.userType)}
                                </span>
                              </td>
                            );
                          }
                          if (colId === "phone") {
                            return (
                              <td key={colId} className={`px-4 ${rowH} whitespace-nowrap text-center`} style={{ maxWidth: defsById.phone.maxWidth }}>
                                <OverflowTooltipText text={user.phone} dir="ltr" />
                              </td>
                            );
                          }
                          if (colId === "status") {
                            const isEnabled = Boolean(user.userStatus);
                            const statusColor = isEnabled ? "#188A4B" : "#C22B3B";
                            const statusBg = isEnabled ? "#E7F6ED" : "#FBE9EB";
                            const statusLabel = isEnabled ? translations[langMode].Enable : translations[langMode].disable;
                            const menuKey = `status-${user.userId}`;
                            const isMenuOpen = openStatusMenuId === menuKey;
                            return (
                              <td key={colId} className={`px-4 ${rowH} whitespace-nowrap text-center`} style={{ maxWidth: defsById.status.maxWidth }}>
                                <div ref={isMenuOpen ? statusMenuRef : null} className="relative inline-block">
                                  <button
                                    type="button"
                                    onClick={() => setOpenStatusMenuId(isMenuOpen ? null : menuKey)}
                                    className="inline-flex items-center gap-1.5"
                                  >
                                    <span
                                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md"
                                      style={{ color: statusColor, background: statusBg }}
                                    >
                                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: statusColor }} />
                                      {statusLabel}
                                    </span>
                                    <ChevronDown size={13} className="text-slate-400" />
                                  </button>
                                  {isMenuOpen && (
                                    <div
                                      className={`absolute ${isRtl ? "right-0" : "left-0"} mt-1 w-40 bg-white rounded-lg border border-slate-200 shadow-lg z-20 py-1`}
                                      onMouseLeave={() => setOpenStatusMenuId(null)}
                                    >
                                      {[
                                        { value: "Enable", label: translations[langMode].Enable, color: "#188A4B" },
                                        { value: "Disable", label: translations[langMode].disable, color: "#C22B3B" },
                                      ].map((s) => (
                                        <button
                                          key={s.value}
                                          type="button"
                                          onClick={() => { handleActionChange(s.value, user.userId); setOpenStatusMenuId(null); }}
                                          className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 flex items-center gap-2 text-slate-700"
                                        >
                                          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: s.color }} />
                                          {s.label}
                                          {(s.value === "Enable") === isEnabled && <Check size={13} className="ml-auto text-blue-600" />}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </td>
                            );
                          }
                          if (colId === "organization") {
                            return (
                              <td key={colId} className={`px-4 ${rowH} whitespace-nowrap text-center`} style={{ maxWidth: defsById.organization.maxWidth }}>
                                <OverflowTooltipText text={user.organization?.organizationName || "—"} />
                              </td>
                            );
                          }
                          if (colId === "department" || colId === "project" || colId === "workflow" || colId === "role") {
                            // Names come straight from the *Details arrays ([{ id, name }]).
                            const detailsByCol = { department: user.departmentDetails, project: user.projectDetails, workflow: user.workflowDetails, role: user.roleDetails };
                            return (
                              <td key={colId} className={`px-4 ${rowH} text-center`} style={{ maxWidth: defsById[colId].maxWidth }}>
                                <MultiValueCell names={detailNames(detailsByCol[colId])} emptyLabel={defsById[colId].label} />
                              </td>
                            );
                          }
                          if (colId === "group" || colId === "agentGroup") {
                            // Assigned Groups: groupListDetails (admin/supervisor/office user).
                            // Agent Group: groupDetails (field agent; absent = no group).
                            return (
                              <td key={colId} className={`px-4 ${rowH} text-center`} style={{ maxWidth: defsById[colId].maxWidth }}>
                                <GroupCell
                                  groups={colId === "group" ? assignedGroupsOf(user) : agentGroupsOf(user)}
                                  membersLabel={translations[langMode].groupMembers || "Group Members"}
                                />
                              </td>
                            );
                          }
                          if (colId === "email" || colId === "employeeId") {
                            const value = user[colId];
                            return (
                              <td key={colId} className={`px-4 ${rowH} whitespace-nowrap text-center`} style={{ maxWidth: defsById[colId].maxWidth }}>
                                {value === null || value === undefined || value === ""
                                  ? <span className="text-slate-400">—</span>
                                  : <OverflowTooltipText text={String(value)} dir={colId === "email" ? "ltr" : undefined} />}
                              </td>
                            );
                          }
                          if (colId === "lastLogin") {
                            return (
                              <td key={colId} className={`px-4 ${rowH} whitespace-nowrap text-slate-500 text-center`} style={{ maxWidth: defsById.lastLogin.maxWidth }}>
                                {lastLoginDate(user.loginHistory)}
                              </td>
                            );
                          }
                          return null;
                        })}
                        {/* Actions - only when user has canUpdate/canDelete (or super_admin) */}
                        {columnVisibility.action && (
                          <td
                            className={`px-4 ${rowH} whitespace-nowrap text-center`}
                            style={{ width: FIXED_COL_WIDTH.action, minWidth: FIXED_COL_WIDTH.action, maxWidth: FIXED_COL_WIDTH.action }}
                          >
                            <div className="inline-flex items-center gap-1.5 sm:gap-3 w-full justify-center">
                              <HoverTooltip text={translations[langMode].viewLoginHistory || "View login history"}>
                                <ActionIconButton
                                  onClick={() => openLoginHistoryModal(user.userId, user.name)}
                                  tone="emerald"
                                  ariaLabel={translations[langMode].viewLoginHistory || "View login history"}
                                  className="!h-7 !w-7 !rounded-md"
                                >
                                  <FiEye size={14} />
                                </ActionIconButton>
                              </HoverTooltip>

                              <HoverTooltip text={translations[langMode].editandupdateuserdetail}>
                                <ActionIconButton
                                  onClick={() => editInfo(user.userId)}
                                  tone="blue"
                                  ariaLabel={translations[langMode].editandupdateuserdetail}
                                  className="!h-7 !w-7 !rounded-md"
                                >
                                  <Pencil size={14} />
                                </ActionIconButton>
                              </HoverTooltip>
                            </div>
                          </td>
                        )}
                        <td aria-hidden="true" className="p-0" />
                      </tr>
                    ))
                  ) : !loading ? (
                    <tr>
                      <td
                        colSpan={tableHeaders.length + 1}
                        className="py-16 h-[200px] align-middle"
                        data-testid="no-users-found-text"
                      >
                        <div className="flex flex-col items-center justify-center text-center">
                          <div className="w-14 h-14 rounded-full flex items-center justify-center mb-3 bg-blue-50 text-blue-600">
                            {hasActiveFilters ? <SearchX size={22} /> : <Inbox size={22} />}
                          </div>
                          <div className="text-sm font-semibold text-slate-800">
                            {missingGridScope
                              ? (translations[langMode].userListNoProjectTitle || "No project selected")
                              : hasActiveFilters
                                ? (translations[langMode].noUsersMatchSearch || "No users match your search")
                                : (translations[langMode].nowuserfound || "No users found")}
                          </div>
                          <div className="text-xs mt-1 max-w-xs text-slate-400">
                            {missingGridScope
                              ? (translations[langMode].userListNoProjectHint || "Select a department and project at the top of the page to see users.")
                              : hasActiveFilters
                                ? (translations[langMode].tryAdjustingFilters || "Try adjusting your filters or search terms to find what you're looking for.")
                                : (translations[langMode].getStartedAddingUser || "Get started by adding your first user.")}
                          </div>
                          {hasActiveFilters || missingGridScope ? null : (
                            hasScreenAccess("USER_MANAGEMENT", "canCreate") && (
                              <button data-button-intent="primary"
                                type="button"
                                onClick={openAddUserModal}
                                className="mt-3 h-8 px-3 rounded-lg text-xs font-medium text-white bg-blue-600"
                              >
                                {translations[langMode].addUser}
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
                
              </table>
            </div>
          </div>

        </div>
      </div>

      {showLoginHistoryModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-[2px] px-4"
          onClick={closeLoginHistoryModal}
        >
          <div
            className={`w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden ${theme === "dark"
              ? "dark-mode border"
              : theme === "high-contrast"
                ? "high-contrast border"
                : "bg-white"
              }`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5 bg-gradient-to-r from-slate-50 to-white">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  {translations[langMode].userActivity || "User Activity"}
                </p>
                <h3 className="text-xl font-semibold text-gray-800">
                  {(translations[langMode].loginHistoryTitle || "Login History")} {selectedUserName ? `- ${selectedUserName}` : ""}
                </h3>
              </div>
              <ModalCloseButton onClick={closeLoginHistoryModal} />
            </div>

            <div className="max-h-[70vh] overflow-auto p-6">
              {loginHistoryLoading ? (
                <div className="py-16 flex flex-col items-center gap-3">
                  <Spinner />
                  <p className="text-sm text-gray-500">
                    {translations[langMode].loadingLoginHistory || "Loading login history..."}
                  </p>
                </div>
              ) : loginHistoryError ? (
                <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3">
                  <p className="text-sm text-red-700 font-medium">{loginHistoryError}</p>
                </div>
              ) : selectedUserLoginHistory.length === 0 ? (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-5 py-8 text-center">
                  <p className="text-base font-semibold text-gray-700">
                    {translations[langMode].noLoginActivityYet || "No login activity yet"}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {translations[langMode].loginHistoryWillAppearWhenSignIn || "Login history will appear here when this user signs in."}
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                    <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                      <p className="text-[11px] font-semibold text-blue-700 uppercase tracking-wide">
                        {translations[langMode].mostRecentLogin || "Most Recent Login"}
                      </p>
                      <p className="mt-2 text-sm font-medium text-slate-800">
                        {formatLoginDate(selectedUserLoginHistory[0]?.loginAt)}
                      </p>
                    </div>
                    <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-4">
                      <p className="text-[11px] font-semibold text-indigo-700 uppercase tracking-wide">
                        {translations[langMode].systemId || "System ID"}
                      </p>
                      <p className="mt-2 text-sm font-medium text-slate-800 break-all">
                        {selectedUserLoginHistory[0]?.systemId || translatedNA}
                      </p>
                    </div>
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
                      <p className="text-[11px] font-semibold text-emerald-700 uppercase tracking-wide">
                        {translations[langMode].loginLocation || "Location"}
                      </p>
                      <p className="mt-2 text-sm font-medium text-slate-800">
                        {resolvedLocations[
                          getLocationKey(
                            selectedUserLoginHistory[0]?.latitude,
                            selectedUserLoginHistory[0]?.longitude
                          )
                        ] || formatCoordinates(
                          selectedUserLoginHistory[0]?.latitude,
                          selectedUserLoginHistory[0]?.longitude
                        )}
                      </p>
                      {getMapUrl(
                        selectedUserLoginHistory[0]?.latitude,
                        selectedUserLoginHistory[0]?.longitude
                      ) && (
                        <a
                          href={getMapUrl(
                            selectedUserLoginHistory[0]?.latitude,
                            selectedUserLoginHistory[0]?.longitude
                          )}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex mt-2 text-sm text-emerald-700 hover:text-emerald-800 underline"
                        >
                          {translations[langMode].openInMap || "Open in map"}
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="overflow-auto rounded-xl border border-gray-200">
                    <table className="w-full min-w-[680px] text-sm">
                      <thead className="bg-gradient-to-r from-gray-50 to-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-start font-semibold text-gray-700">
                            {translations[langMode].loginTime || "Login Time"}
                          </th>
                          <th className="px-4 py-3 text-start font-semibold text-gray-700">
                            {translations[langMode].systemId || "System ID"}
                          </th>
                          <th className="px-4 py-3 text-start font-semibold text-gray-700">
                            {translations[langMode].loginLocation || "Location"}
                          </th>
                          <th className="px-4 py-3 text-start font-semibold text-gray-700">
                            {translations[langMode].map || "Map"}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100">
                        {selectedUserLoginHistory.map((entry, index) => {
                          const mapUrl = getMapUrl(entry?.latitude, entry?.longitude);
                          const locationText =
                            resolvedLocations[
                              getLocationKey(entry?.latitude, entry?.longitude)
                            ] || formatCoordinates(entry?.latitude, entry?.longitude);
                          return (
                            <tr key={`${entry?.loginAt || "time"}-${index}`} className="hover:bg-gray-50/70 transition-colors">
                              <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{formatLoginDate(entry?.loginAt)}</td>
                              <td className="px-4 py-3 text-gray-700 break-all">
                                <span className="inline-flex px-2 py-1 rounded-md bg-slate-100 text-slate-700 text-xs font-medium">
                                  {entry?.systemId || translatedNA}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-700 max-w-[360px]" title={locationText}>
                                <span className="block truncate max-w-[360px]">
                                  {locationText}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-gray-700">
                                {mapUrl ? (
                                  <a
                                    href={mapUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex px-2.5 py-1 rounded-md border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors text-xs font-semibold"
                                  >
                                    {translations[langMode].open || "Open"}
                                  </a>
                                ) : (
                                  <span className="text-xs text-gray-400">{translatedNA}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showUserFormModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 backdrop-blur-[3px] px-4 py-6"
          role="dialog"
          aria-modal="true"
        >
          <div
            className={`relative flex w-full max-w-6xl max-h-[88vh] overflow-hidden rounded-xl border shadow-2xl ${theme === "dark"
              ? "dark-mode border-slate-700"
              : theme === "high-contrast"
                ? "high-contrast border"
                : "bg-white border-gray-200"
              }`}
          >
            <ModalCloseButton
              onClick={closeUserFormModal}
              ariaLabel={translations[langMode].userFormCloseAria}
              className={`absolute top-4 z-20 ${isRtl ? "left-4" : "right-4"}`}
            />
            <div className="user-management-modal-scroll w-full max-h-[88vh] overflow-y-auto overflow-x-hidden">
              <AddUser
                userId={modalUserId}
                isModal
                onClose={closeUserFormModal}
                onSuccess={handleUserFormSuccess}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};
export default UserList;
